import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import * as actions from 'app/actions/sap-globals';
import { initialMetaDataFactory, MetaData } from 'app/models/reducers/meta-data';
import { MetaDataReducerUtils } from '../utils/reducers/meta-data.utils';
import {
  ArrayUtils,
  CatalogGroupDto,
  EquipmentOverviewResponseDto,
  FunctionalLocation,
  MaintenanceNotificationCode,
  NotificationType,
  SapActivityTypeDto,
  SapLanguage,
  SapMaintenancePlannerGroupDto,
  SapOrderTypeDto,
  SapResponseKeys,
  SapSystemStatusDto,
  SapUserStatusDto,
  SapWorkCenterDto
} from '@festo-ax/smartenance-shared-ui';
import { MachineBriefData } from '../models/api/machines';
import { SapUtils } from '../utils/reducers/sap.utils';

export interface SapDataContainer<T> {
  data: T;
  meta: MetaData;
}

export type MultiData<T, K extends string> = { [key in K]: T };

export interface SapGlobalState {
  systemStates: SapDataContainer<MultiData<SapSystemStatusDto[], SapResponseKeys<'getAllSystemStatus'>>>;
  userStates: SapDataContainer<MultiData<SapUserStatusDto[], SapResponseKeys<'getAllUserStatus'>>>;
  maintenanceWorkcenters: SapDataContainer<SapWorkCenterDto[]>;
  manufacturingWorkcenters: SapDataContainer<SapWorkCenterDto[]>;
  functionalLocations: SapDataContainer<FunctionalLocation[]>;
  notificationTypes: SapDataContainer<NotificationType[]>;
  sapMachineNames: SapDataContainer<MachineBriefData[]>;
  sapAsyncMachines: SapDataContainer<EquipmentOverviewResponseDto[]>;
  orderTypes: SapDataContainer<SapOrderTypeDto[]>;
  plannerGroups: SapDataContainer<SapMaintenancePlannerGroupDto[]>;
  failureTypes: SapDataContainer<MaintenanceNotificationCode[]>;
  catalogGroups: SapDataContainer<CatalogGroupDto[]>;
  activityTypes: SapDataContainer<SapActivityTypeDto[]>;
  languageTypes: SapDataContainer<SapLanguage[]>;
}

export const initialState: SapGlobalState = {
  systemStates: {
    data: {
      maintenanceNotification: [],
      maintenanceOrderOperation: [],
      maintenanceOrder: []
    },
    meta: initialMetaDataFactory()
  },
  userStates: {
    data: {
      maintenanceOrderOperation: [],
      maintenanceNotification: [],
      maintenanceOrder: []
    },
    meta: initialMetaDataFactory()
  },
  maintenanceWorkcenters: {
    data: [],
    meta: initialMetaDataFactory()
  },
  manufacturingWorkcenters: {
    data: [],
    meta: initialMetaDataFactory()
  },
  functionalLocations: {
    data: [],
    meta: initialMetaDataFactory()
  },
  notificationTypes: {
    data: [],
    meta: initialMetaDataFactory()
  },
  sapMachineNames: {
    data: [],
    meta: initialMetaDataFactory()
  },
  sapAsyncMachines: {
    data: [],
    meta: initialMetaDataFactory()
  },
  orderTypes: {
    data: [],
    meta: initialMetaDataFactory()
  },
  plannerGroups: {
    data: [],
    meta: initialMetaDataFactory()
  },
  failureTypes: {
    data: [],
    meta: initialMetaDataFactory()
  },
  catalogGroups: {
    data: [],
    meta: initialMetaDataFactory()
  },
  activityTypes: {
    data: [],
    meta: initialMetaDataFactory()
  },
  languageTypes: {
    data: [],
    meta: initialMetaDataFactory()
  },
};

export const featureSelectorKey = 'sap-global';

export const sapGlobalFeature = createFeature({
  name: featureSelectorKey,
  reducer: createReducer(
    initialState,

    on(actions.getSystemStates, (state) => ({
      ...state, systemStates: { ...state.systemStates, meta: MetaDataReducerUtils.loadingStart(state.systemStates.meta) },
    })),
    on(actions.getSystemStatesSuccess, (state, action) => ({
      ...state,
      systemStates: {
        ...state.systemStates,
        data: {
          ...state.systemStates.data,
          maintenanceNotification: ArrayUtils.isNotEmpty(action.response?.data?.maintenanceNotification) ? action.response.data.maintenanceNotification : [],
          maintenanceOrderOperation: ArrayUtils.isNotEmpty(action.response?.data?.maintenanceOrderOperation) ? action.response.data.maintenanceOrderOperation : [],
          maintenanceOrder: ArrayUtils.isNotEmpty(action.response?.data?.maintenanceOrder) ? action.response.data.maintenanceOrder : [],
        },
        meta: MetaDataReducerUtils.loadingComplete(state.systemStates.meta)
      },
    })),
    on(actions.getSystemStatesFail, (state) => ({
      ...state,
      systemStates: {
        ...state.systemStates,
        meta: MetaDataReducerUtils.loadingComplete(state.systemStates.meta, true)
      },
    })),

    on(actions.getUserStates, (state) => ({
      ...state, userStates: { ...state.userStates, meta: MetaDataReducerUtils.loadingStart(state.userStates.meta) },
    })),
    on(actions.getUserStatesSuccess, (state, action) => ({
      ...state,
      userStates: {
        ...state.userStates,
        data: {
          ...state.systemStates.data,
          maintenanceNotification: ArrayUtils.isNotEmpty(action.response?.data?.maintenanceNotification) ? action.response.data.maintenanceNotification : [],
          maintenanceOrderOperation: ArrayUtils.isNotEmpty(action.response?.data?.maintenanceOrderOperation) ? action.response.data.maintenanceOrderOperation : [],
          maintenanceOrder: ArrayUtils.isNotEmpty(action.response?.data?.maintenanceOrder) ? action.response.data.maintenanceOrder : [],
        },
        meta: MetaDataReducerUtils.loadingComplete(state.userStates.meta)
      },
    })),
    on(actions.getUserStatesFail, (state) => ({
      ...state,
      userStates: {
        ...state.userStates,
        meta: MetaDataReducerUtils.loadingComplete(state.userStates.meta, true)
      },
    })),

    on(actions.getManufacturingWorkcenterList, (state) => ({
      ...state, manufacturingWorkcenters: { ...state.manufacturingWorkcenters, meta: MetaDataReducerUtils.loadingStart(state.manufacturingWorkcenters.meta) },
    })),
    on(actions.getManufacturingWorkcenterListSuccess, (state, action) => ({
      ...state,
      manufacturingWorkcenters: {
        ...state.manufacturingWorkcenters,
        data: action.response.data.result.items,
        meta: MetaDataReducerUtils.loadingComplete(state.manufacturingWorkcenters.meta)
      },
    })),
    on(actions.getManufacturingWorkcenterListFail, (state) => ({
      ...state,
      manufacturingWorkcenters: {
        ...state.manufacturingWorkcenters,
        meta: MetaDataReducerUtils.loadingComplete(state.manufacturingWorkcenters.meta, true)
      },
    })),

    on(actions.getMaintenanceWorkcenterList, (state) => ({
      ...state, maintenanceWorkcenters: { ...state.maintenanceWorkcenters, meta: MetaDataReducerUtils.loadingStart(state.maintenanceWorkcenters.meta) },
    })),
    on(actions.getMaintenanceWorkcenterListSuccess, (state, action) => ({
      ...state,
      maintenanceWorkcenters: {
        ...state.maintenanceWorkcenters,
        data: action.response.data.items,
        meta: MetaDataReducerUtils.loadingComplete(state.maintenanceWorkcenters.meta)
      },
    })),
    on(actions.getMaintenanceWorkcenterListFail, (state) => ({
      ...state,
      maintenanceWorkcenters: {
        ...state.maintenanceWorkcenters,
        meta: MetaDataReducerUtils.loadingComplete(state.maintenanceWorkcenters.meta, true)
      },
    })),

    on(actions.getFunctionalLocations, (state) => ({
      ...state, functionalLocations: { ...state.functionalLocations, meta: MetaDataReducerUtils.loadingStart(state.functionalLocations.meta) },
    })),
    on(actions.getFunctionalLocationsSuccess, (state, action) => ({
      ...state,
      functionalLocations: {
        ...state.functionalLocations,
        data: action.response.data.functionalLocations,
        meta: MetaDataReducerUtils.loadingComplete(state.functionalLocations.meta)
      },
    })),
    on(actions.getFunctionalLocationsFail, (state) => ({
      ...state,
      functionalLocations: {
        ...state.functionalLocations,
        meta: MetaDataReducerUtils.loadingComplete(state.functionalLocations.meta, true)
      },
    })),

    on(actions.getNotificationTypes, (state) => ({
      ...state, notificationTypes: { ...state.notificationTypes, meta: MetaDataReducerUtils.loadingStart(state.notificationTypes.meta) },
    })),
    on(actions.getNotificationTypesSuccess, (state, action) => ({
      ...state,
      notificationTypes: {
        ...state.notificationTypes,
        data: action.response.data.notificationTypes,
        meta: MetaDataReducerUtils.loadingComplete(state.notificationTypes.meta)
      },
    })),
    on(actions.getNotificationTypesFail, (state) => ({
      ...state,
      notificationTypes: {
        ...state.notificationTypes,
        meta: MetaDataReducerUtils.loadingComplete(state.notificationTypes.meta, true)
      },
    })),

    on(actions.getSapMachineNames, (state) => ({
      ...state, sapMachineNames: { ...state.sapMachineNames, meta: MetaDataReducerUtils.loadingStart(state.sapMachineNames.meta) },
    })),
    on(actions.getSapMachineNamesSuccess, (state, action) => ({
      ...state,
      sapMachineNames: {
        ...state.sapMachineNames,
        data: SapUtils.mapMachineNames(action.response.machines),
        meta: MetaDataReducerUtils.loadingComplete(state.sapMachineNames.meta)
      },
    })),
    on(actions.getSapMachineNamesFail, (state) => ({
      ...state,
      sapMachineNames: {
        ...state.sapMachineNames,
        meta: MetaDataReducerUtils.loadingComplete(state.sapMachineNames.meta, true)
      },
    })),

    on(actions.getAsyncSapMachines, (state) => ({
      ...state, sapAsyncMachines: { ...state.sapAsyncMachines, meta: MetaDataReducerUtils.loadingStart(state.sapAsyncMachines.meta) },
    })),
    on(actions.getAsyncSapMachinesSuccess, (state, action) => ({
      ...state,
      sapAsyncMachines: {
        ...state.sapAsyncMachines,
        data: action.machines,
        meta: MetaDataReducerUtils.loadingComplete(state.sapAsyncMachines.meta)
      },
    })),
    on(actions.getAsyncSapMachinesFail, (state) => ({
      ...state,
      sapAsyncMachines: {
        ...state.sapAsyncMachines,
        meta: MetaDataReducerUtils.loadingComplete(state.sapAsyncMachines.meta, true)
      },
    })),

    on(actions.getOrderTypes, (state) => ({
      ...state, orderTypes: { ...state.orderTypes, meta: MetaDataReducerUtils.loadingStart(state.orderTypes.meta) },
    })),
    on(actions.getOrderTypesSuccess, (state, action) => ({
      ...state,
      orderTypes: {
        ...state.orderTypes,
        data: action.response.data.items,
        meta: MetaDataReducerUtils.loadingComplete(state.orderTypes.meta)
      },
    })),
    on(actions.getOrderTypesFail, (state) => ({
      ...state,
      orderTypes: {
        ...state.orderTypes,
        meta: MetaDataReducerUtils.loadingComplete(state.orderTypes.meta, true)
      },
    })),

    on(actions.getPlannerGroups, (state) => ({
      ...state, plannerGroups: { ...state.plannerGroups, meta: MetaDataReducerUtils.loadingStart(state.plannerGroups.meta) },
    })),
    on(actions.getPlannerGroupsSuccess, (state, action) => ({
      ...state,
      plannerGroups: {
        ...state.plannerGroups,
        data: action.response.data.items,
        meta: MetaDataReducerUtils.loadingComplete(state.plannerGroups.meta)
      },
    })),
    on(actions.getPlannerGroupsFail, (state) => ({
      ...state,
      plannerGroups: {
        ...state.plannerGroups,
        meta: MetaDataReducerUtils.loadingComplete(state.plannerGroups.meta, true)
      },
    })),

    on(actions.getCatalogGroups, (state) => ({
      ...state, catalogGroups: { ...state.catalogGroups, meta: MetaDataReducerUtils.loadingStart(state.catalogGroups.meta) },
    })),
    on(actions.getCatalogGroupsSuccess, (state, action) => ({
      ...state,
      catalogGroups: {
        ...state.catalogGroups,
        data: action.response.data.items,
        meta: MetaDataReducerUtils.loadingComplete(state.catalogGroups.meta)
      },
    })),
    on(actions.getCatalogGroupsFail, (state) => ({
      ...state,
      catalogGroups: {
        ...state.catalogGroups,
        meta: MetaDataReducerUtils.loadingComplete(state.catalogGroups.meta, true)
      },
    })),

    on(actions.getFailureTypes, (state) => ({
      ...state, failureTypes: { ...state.failureTypes, meta: MetaDataReducerUtils.loadingStart(state.failureTypes.meta) },
    })),
    on(actions.getFailureTypesSuccess, (state, action) => ({
      ...state,
      failureTypes: {
        ...state.failureTypes,
        data: action.response.data.maintenanceNotificationCodes,
        meta: MetaDataReducerUtils.loadingComplete(state.failureTypes.meta)
      },
    })),
    on(actions.getFailureTypesFail, (state) => ({
      ...state,
      failureTypes: {
        ...state.failureTypes,
        meta: MetaDataReducerUtils.loadingComplete(state.failureTypes.meta, true)
      },
    })),

    on(actions.getActivityTypes, (state) => ({
      ...state, activityTypes: { ...state.activityTypes, meta: MetaDataReducerUtils.loadingStart(state.activityTypes.meta) },
    })),
    on(actions.getActivityTypesSuccess, (state, action) => ({
      ...state,
      activityTypes: {
        ...state.activityTypes,
        data: action.response.data.result.items,
        meta: MetaDataReducerUtils.loadingComplete(state.activityTypes.meta)
      },
    })),
    on(actions.getActivityTypesFail, (state) => ({
      ...state,
      activityTypes: {
        ...state.activityTypes,
        meta: MetaDataReducerUtils.loadingComplete(state.activityTypes.meta, true)
      },
    })),

    on(actions.getLanguageTypes, (state) => ({
      ...state, languageTypes: { ...state.languageTypes, meta: MetaDataReducerUtils.loadingStart(state.languageTypes.meta) },
    })),
    on(actions.getLanguageTypesSuccess, (state, action) => ({
      ...state,
      languageTypes: {
        ...state.languageTypes,
        data: action.response.data.languages,
        meta: MetaDataReducerUtils.loadingComplete(state.languageTypes.meta)
      },
    })),
    on(actions.getLanguageTypesFail, (state) => ({
      ...state,
      languageTypes: {
        ...state.languageTypes,
        meta: MetaDataReducerUtils.loadingComplete(state.languageTypes.meta, true)
      },
    })),
  )
});

export const {
  reducer,
  name,

  selectSystemStates,
  selectUserStates,
  selectMaintenanceWorkcenters,
  selectManufacturingWorkcenters,
  selectFunctionalLocations,
  selectNotificationTypes,
  selectSapMachineNames,
  selectSapAsyncMachines,
  selectOrderTypes,
  selectPlannerGroups,
  selectCatalogGroups,
  selectFailureTypes,
  selectActivityTypes,
  selectLanguageTypes,
} = sapGlobalFeature;

export const getNotificationSystemStates = createSelector(selectSystemStates, state => state.data.maintenanceNotification);
export const getOrderSystemStates = createSelector(selectSystemStates, state => state.data.maintenanceOrder);
export const getOrderOperationSystemStates = createSelector(selectSystemStates, state => state.data.maintenanceOrderOperation);
export const getSystemStatesLoading = createSelector(selectSystemStates, state => state.meta.loading);

export const getNotificationUserStates = createSelector(selectUserStates, state => state.data.maintenanceNotification);
export const getOrderUserStates = createSelector(selectUserStates, state => state.data.maintenanceOrder);
export const getOrderOperationUserStates = createSelector(selectUserStates, state => state.data.maintenanceOrderOperation);
export const getUserStatesLoading = createSelector(selectUserStates, state => state.meta.loading);

export const getManufacturingWorkcenters = createSelector(selectManufacturingWorkcenters, state => state.data);
export const getManufacturingWorkcentersLoading = createSelector(selectManufacturingWorkcenters, state => state.meta.loading);

export const getMaintenanceWorkcenters = createSelector(selectMaintenanceWorkcenters, state => state.data);
export const getMaintenanceWorkcentersLoading = createSelector(selectMaintenanceWorkcenters, state => state.meta.loading);

export const getFunctionalLocations = createSelector(selectFunctionalLocations, state => state.data);
export const getFunctionalLocationsLoading = createSelector(selectFunctionalLocations, state => state.meta.loading);

export const getNotificationTypes = createSelector(selectNotificationTypes, state => state.data);
export const getNotificationTypesLoading = createSelector(selectNotificationTypes, state => state.meta.loading);

export const getSapMachineNames = createSelector(selectSapMachineNames, state => state.data);
export const getSapMachineNamesLoading = createSelector(selectSapMachineNames, state => state.meta.loading);

export const getAsyncSapMachines = createSelector(selectSapAsyncMachines, state => state.data);
export const getAsyncSapMachinesLoading = createSelector(selectSapAsyncMachines, state => state.meta.loading);

export const getOrderTypes = createSelector(selectOrderTypes, state => state.data);
export const getOrderTypesLoading = createSelector(selectOrderTypes, state => state.meta.loading);

export const getPlannerGroups = createSelector(selectPlannerGroups, state => state.data);
export const getPlannerGroupsLoading = createSelector(selectPlannerGroups, state => state.meta.loading);

export const getFailureTypes = createSelector(selectFailureTypes, state => state.data);
export const getFailureTypesLoading = createSelector(selectFailureTypes, state => state.meta.loading);

export const getCatalogGroups = createSelector(selectCatalogGroups, state => state.data);
export const getCatalogGroupsLoading = createSelector(selectCatalogGroups, state => state.meta.loading);

export const getActivityTypes = createSelector(selectActivityTypes, state => state.data);
export const getActivityTypesLoading = createSelector(selectActivityTypes, state => state.meta.loading);

export const getLanguageTypes = createSelector(selectLanguageTypes, state => state.data);
export const getLanguageTypesLoading = createSelector(selectLanguageTypes, state => state.meta.loading);
